// Global lighting profile toggle (V1 / V2 / V3).
//
// V1 — the original tuning. Don't touch.
// V2 — brighter, more open feel. Lifts exposure and IBL contribution so
//      surfaces catch a bit more soft light from the env without losing
//      the rim character.
// V3 — brighter still. Closer to a "showroom" feel — the dark base reads
//      as charcoal rather than near-black, highlights are punchy. Good
//      for screenshots / hero shots where you want every object visible
//      without leaning on the user to crank the dot grid + theme.
//
// We keep the change surface tiny and renderer-side only:
//   • renderer.toneMappingExposure
//   • scene.environmentIntensity
// No material PBR values are mutated; the OneQode shader keeps its existing
// onBeforeCompile path and rim/band uniforms.
//
// Persisted in localStorage under `oneqode-lighting-version` so the toggle
// survives reloads and is shared across tabs (Scene / Iso / 3D Icon).

import * as THREE from "three";

export type LightingVersion = "v1" | "v2" | "v3" | "v4";

const KEY = "oneqode-lighting-version";

export function loadLightingVersion(): LightingVersion {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "v2") return "v2";
    if (v === "v3") return "v3";
    if (v === "v4") return "v4";
    return "v1";
  } catch {
    return "v1";
  }
}

export function saveLightingVersion(v: LightingVersion): void {
  try { localStorage.setItem(KEY, v); } catch { /* ignore */ }
}

/**
 * Apply the renderer-side parameters for the given version. Safe to call
 * any time after the renderer + scene exist; tabs invoke this once at
 * mount and again whenever the user flips the toggle.
 *
 *   v1: exposure 1.10, env intensity = THEMES.dark.environmentIntensity (default).
 *   v2: exposure 1.35 + env intensity 1.4× — overall brighter so highlights
 *       open up the milky satin without washing the blacks.
 *   v3: exposure 1.65 + env intensity 1.85× — showroom-bright; chassis
 *       reads as deep charcoal, highlights pop. Useful for hero shots.
 *   v4: hero/product-shot — near-black bg (#050507), warm key, cool fill,
 *       cool rim, dimmed dot grid. Pairs with applyLightingV4Extras() for
 *       the parts that need access to lights / dotMat / ground.
 */
export function applyLightingVersion(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  version: LightingVersion,
  baseEnvIntensity: number,
): void {
  switch (version) {
    case "v4":
      renderer.toneMappingExposure = 1.20;
      scene.environmentIntensity = baseEnvIntensity * 0.65;
      break;
    case "v3":
      renderer.toneMappingExposure = 1.65;
      scene.environmentIntensity = baseEnvIntensity * 1.85;
      break;
    case "v2":
      renderer.toneMappingExposure = 1.35;
      scene.environmentIntensity = baseEnvIntensity * 1.4;
      break;
    default:
      renderer.toneMappingExposure = 1.10;
      scene.environmentIntensity = baseEnvIntensity;
      break;
  }
}

/**
 * Extras for v4 that need handles only the builders own (lights, ground,
 * dot-grid material). When active, this rewires the scene toward an
 * Apple-style hero look: near-black background, warm key, cool fill/rim,
 * dimmed dot-grid. When inactive, applies `restoreFromTheme` so the other
 * versions get back the palette they expect.
 */
export type LightingV4Lights = {
  hemi: THREE.HemisphereLight;
  key: THREE.DirectionalLight;
  fill: THREE.DirectionalLight;
  rim: THREE.DirectionalLight;
  floorGlow?: THREE.PointLight;
};

export type LightingV4ThemeFallback = {
  bg: number;
  fogNear: number;
  fogFar: number;
  ground: number;
  dot: number;
  hemiSky: number;
  hemiGround: number;
  hemiIntensity: number;
  keyColor: number;
  keyIntensity: number;
  fillIntensity: number;
  rimColor: number;
  rimIntensity: number;
  floorGlowColor: number;
  floorGlowIntensity: number;
};

export function applyLightingV4Extras(
  scene: THREE.Scene,
  renderer: THREE.WebGLRenderer,
  lights: LightingV4Lights,
  dotMat: THREE.MeshBasicMaterial,
  ground: THREE.Mesh,
  version: LightingVersion,
  fallback: LightingV4ThemeFallback,
): void {
  const groundMat = ground.material as THREE.MeshStandardMaterial;
  if (version === "v4") {
    const bg = 0x050507;
    renderer.setClearColor(bg, 1);
    scene.background = new THREE.Color(bg);
    scene.fog = new THREE.Fog(bg, 120, 320);

    groundMat.color.setHex(0x07080a);
    groundMat.envMapIntensity = 0.03;
    groundMat.needsUpdate = true;

    dotMat.color.setHex(0x6a6a72);
    dotMat.opacity = Math.min(dotMat.opacity, 0.55);

    lights.hemi.color.setHex(0x1b1d24);
    lights.hemi.groundColor.setHex(0x05060a);
    lights.hemi.intensity = 0.35;

    lights.key.color.setHex(0xffe7c8);
    lights.key.intensity = 3.2;

    lights.fill.color.setHex(0x6f87b0);
    lights.fill.intensity = 0.25;

    lights.rim.color.setHex(0x9bb6e0);
    lights.rim.intensity = 0.9;

    if (lights.floorGlow) {
      lights.floorGlow.color.setHex(0x3a4a66);
      lights.floorGlow.intensity = 0.25;
    }
  } else {
    // Restore from theme palette so v1/v2/v3 see the values they expect.
    renderer.setClearColor(fallback.bg, 1);
    scene.background = new THREE.Color(fallback.bg);
    scene.fog = new THREE.Fog(fallback.bg, fallback.fogNear, fallback.fogFar);

    groundMat.color.setHex(fallback.ground);
    groundMat.envMapIntensity = 0.05;
    groundMat.needsUpdate = true;

    dotMat.color.setHex(fallback.dot);

    lights.hemi.color.setHex(fallback.hemiSky);
    lights.hemi.groundColor.setHex(fallback.hemiGround);
    lights.hemi.intensity = fallback.hemiIntensity;
    lights.key.color.setHex(fallback.keyColor);
    lights.key.intensity = fallback.keyIntensity;
    lights.fill.intensity = fallback.fillIntensity;
    lights.rim.color.setHex(fallback.rimColor);
    lights.rim.intensity = fallback.rimIntensity;
    if (lights.floorGlow) {
      lights.floorGlow.color.setHex(fallback.floorGlowColor);
      lights.floorGlow.intensity = fallback.floorGlowIntensity;
    }
  }
}
